import { usePrefetchEnabled } from "@ui/hooks/use-prefetch-enabled";
import { isPrefetchableHref, PREFETCH_HOVER_DELAY_MS } from "@ui/lib/prefetch-config";
import { type PrefetchProps, type PrefetchTrigger, usePrefetch } from "@ui/lib/prefetch-context";
import { useEffect, useMemo, useRef } from "react";

const EMPTY_TRIGGERS: ReadonlySet<PrefetchTrigger> = new Set();
const DEFAULT_TRIGGERS: ReadonlySet<PrefetchTrigger> = new Set(["mount", "hover"]);

function resolveTriggers(prefetch: PrefetchProps["prefetch"]): ReadonlySet<PrefetchTrigger> {
  if (!prefetch) return EMPTY_TRIGGERS;
  if (prefetch === true) return DEFAULT_TRIGGERS;
  if (typeof prefetch === "string") return new Set([prefetch]);
  return new Set(prefetch);
}

export interface PrefetchTriggerHandlers {
  hoverHandlers?: { onHoverStart: () => void; onHoverEnd: () => void };
  pressHandlers?: { onPressStart: () => void };
}

/**
 * Wires mount/hover/click prefetch triggers for any hoverable/pressable
 * react-aria element (Link, MenuItem, ...). The mount trigger fires as an
 * effect; hover and press handlers are returned for the caller to mergeProps
 * into the element.
 */
export function usePrefetchTriggers(
  href: string | undefined,
  { prefetch: prefetchProp, cacheFor, cacheTags }: PrefetchProps,
): PrefetchTriggerHandlers {
  const prefetch = usePrefetch();
  const prefetchEnabled = usePrefetchEnabled();
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggers = useMemo(() => resolveTriggers(prefetchProp), [prefetchProp]);

  const canPrefetch = prefetchEnabled && triggers.size > 0 && isPrefetchableHref(href);

  const triggerPrefetch = useMemo(() => {
    return () => {
      if (!canPrefetch || !href) return;
      prefetch(href, { cacheFor, cacheTags });
    };
  }, [canPrefetch, href, cacheFor, cacheTags, prefetch]);

  useEffect(() => {
    if (!canPrefetch || !triggers.has("mount")) return;
    triggerPrefetch();
  }, [canPrefetch, triggers, triggerPrefetch]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const hoverHandlers = useMemo(() => {
    if (!canPrefetch || !triggers.has("hover")) return undefined;
    return {
      onHoverStart: () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = setTimeout(triggerPrefetch, PREFETCH_HOVER_DELAY_MS);
      },
      onHoverEnd: () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
      },
    };
  }, [canPrefetch, triggers, triggerPrefetch]);

  const pressHandlers = useMemo(() => {
    if (!canPrefetch || !triggers.has("click")) return undefined;
    return { onPressStart: triggerPrefetch };
  }, [canPrefetch, triggers, triggerPrefetch]);

  return { hoverHandlers, pressHandlers };
}
